import { useState, type ReactNode } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Switch from '@mui/material/Switch';

import SoftBox from 'components/SoftBox';
import SoftButton from 'components/SoftButton';
import SoftInput from 'components/SoftInput';
import SoftTypography from 'components/SoftTypography';

import { interpolateTemplate } from 'lib/i18n/dictionary';
import {
  buildGallerySettingsInput,
  deleteGalleryRequest,
  galleryErrorMessage,
  GALLERY_AUTO_DELETE,
  FREE_GALLERY_DOWNLOADS,
  GALLERY_DOWNLOADS,
  GALLERY_LANGUAGES,
  gallerySettingsForm,
  isPaidSettingLocked,
  GALLERY_PASSWORD_MAX_LENGTH,
  updateGalleryRequest,
  type Gallery,
  type GallerySettingsForm,
} from 'gallery';

import type { GalleryAction } from './gallery-page';
import {
  ConfirmDialog,
  FieldLabel,
  notifyGalleryError,
  notifyGallerySuccess,
  useGalleryText,
} from './gallery-ui';
import { PlanLockBadge } from './plan-lock-badge';

function settingsError(result: ReturnType<typeof buildGallerySettingsInput>) {
  return 'error' in result ? result.error : null;
}

interface GallerySettingsDialogProps {
  gallery: Gallery;
  token?: string;
  run: GalleryAction;
  onClose: () => void;
  onDeleted: () => void;
}

function Group({ title, children }: { title: string; children: ReactNode }) {
  return (
    <SoftBox display="flex" flexDirection="column" gap={1.5}>
      <SoftTypography variant="overline" fontWeight="bold">
        {title}
      </SoftTypography>
      {children}
    </SoftBox>
  );
}

// Every setting in one dialog; "Done" saves what changed. Delete lives here (outlined red).
export function GallerySettingsDialog({
  gallery,
  token,
  run,
  onClose,
  onDeleted,
}: GallerySettingsDialogProps) {
  const { t, plural } = useGalleryText();
  const s = t.settings;
  const [form, setForm] = useState<GallerySettingsForm>(() => gallerySettingsForm(gallery));
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const formError = settingsError(buildGallerySettingsInput(gallery, form));
  const limitInvalid = formError === 'selectionLimit';
  const passwordInvalid = formError === 'password';
  // Locked only while it stays off: a permission frozen on after a downgrade can be switched off.
  const hidingLocked = isPaidSettingLocked(gallery.planFeatures, 'hiddenPhotos', !form.allowHiding);
  // Expiry and auto-delete on a Plan without them (decision 9): what is set shows and can be
  // cleared or turned off; setting or changing it needs Pro.
  const expiryPlanless = gallery.planFeatures?.expiry === false;
  const autoDeleteLocked = (value: GallerySettingsForm['autoDelete']) =>
    expiryPlanless && value !== 'OFF' && value !== gallery.autoDelete;
  // Downloads with web copies and a selection limit need Pro; originals only, off and no limit are
  // free, and a value kept from before a downgrade stays (decision 9).
  const downloadsPlanless = gallery.planFeatures?.downloads === false;
  const downloadsLocked = (value: GallerySettingsForm['downloads']) =>
    downloadsPlanless && !FREE_GALLERY_DOWNLOADS.includes(value) && value !== gallery.downloads;
  const limitPlanless = gallery.planFeatures?.selectionLimit === false;
  const limitLocked = isPaidSettingLocked(gallery.planFeatures, 'selectionLimit', !gallery.selectionLimit);

  const set = <K extends keyof GallerySettingsForm>(key: K, value: GallerySettingsForm[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const save = async () => {
    const result = buildGallerySettingsInput(gallery, form);
    const error = settingsError(result);
    if (error) {
      notifyGalleryError(t.title, error === 'password' ? s.passwordInvalid : s.selectionLimitInvalid);
      return;
    }
    if (!result.ok) {
      return;
    }
    if (!Object.keys(result.input).length) {
      onClose();
      return;
    }
    setIsSaving(true);
    const ok = await run(() => updateGalleryRequest(gallery.uuid, result.input, token), {
      success: s.saved,
    });
    setIsSaving(false);
    if (ok) {
      onClose();
    }
  };

  // Not through `run`: the page closes at once, so nothing reloads the deleted gallery.
  const remove = async () => {
    setIsDeleting(true);
    try {
      await deleteGalleryRequest(gallery.uuid, token);
      notifyGallerySuccess(t.title, s.deleted);
      onDeleted();
    } catch (error) {
      notifyGalleryError(t.title, galleryErrorMessage(error, t.errors));
      setIsDeleting(false);
      setIsDeleteOpen(false);
    }
  };

  return (
    <Dialog
      open
      onClose={isSaving ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      data-testid="gallery-settings-dialog"
    >
      <DialogTitle>{s.title}</DialogTitle>
      <DialogContent>
        <SoftBox display="flex" flexDirection="column" gap={2} pt={1}>
          <SoftBox>
            <FieldLabel htmlFor="gallery-settings-title">{s.titleLabel}</FieldLabel>
            <SoftInput
              id="gallery-settings-title"
              value={form.title}
              placeholder={s.titlePlaceholder}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                set('title', event.target.value)
              }
              inputProps={{ 'data-testid': 'gallery-settings-title' }}
            />
          </SoftBox>
          <SoftBox>
            <FieldLabel htmlFor="gallery-settings-date">{s.shootDateLabel}</FieldLabel>
            <SoftInput
              id="gallery-settings-date"
              type="date"
              value={form.shootDate}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                set('shootDate', event.target.value)
              }
              inputProps={{ 'data-testid': 'gallery-settings-date' }}
            />
          </SoftBox>
          <Group title={s.access}>
            <SoftBox>
              <FieldLabel htmlFor="gallery-settings-password">{s.passwordLabel}</FieldLabel>
              <SoftInput
                id="gallery-settings-password"
                value={form.password}
                placeholder={s.passwordPlaceholder}
                error={passwordInvalid}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  set('password', event.target.value)
                }
                inputProps={{
                  maxLength: GALLERY_PASSWORD_MAX_LENGTH,
                  autoComplete: 'off',
                  'data-testid': 'gallery-settings-password',
                }}
              />
              {passwordInvalid ? (
                <SoftTypography variant="caption" color="error">
                  {s.passwordInvalid}
                </SoftTypography>
              ) : null}
            </SoftBox>
            <SoftBox>
              <SoftBox display="flex" alignItems="center" gap={1}>
                <FieldLabel htmlFor="gallery-settings-expires">{s.expiresLabel}</FieldLabel>
                {expiryPlanless ? <PlanLockBadge testId="gallery-settings-expires-lock" /> : null}
              </SoftBox>
              <SoftBox display="flex" alignItems="center" gap={1}>
                <SoftBox flex={1}>
                  <SoftInput
                    id="gallery-settings-expires"
                    type="date"
                    value={form.expiresDate}
                    disabled={expiryPlanless}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      set('expiresDate', event.target.value)
                    }
                    inputProps={{
                      disabled: expiryPlanless,
                      'data-testid': 'gallery-settings-expires',
                    }}
                  />
                </SoftBox>
                {expiryPlanless && form.expiresDate ? (
                  <SoftButton
                    variant="text"
                    color="info"
                    size="small"
                    onClick={() => set('expiresDate', '')}
                    data-testid="gallery-settings-expires-clear"
                  >
                    {s.expiresClear}
                  </SoftButton>
                ) : null}
              </SoftBox>
              <SoftTypography variant="caption" color="text">
                {expiryPlanless ? s.expiryLockedHint : s.expiresHint}
              </SoftTypography>
            </SoftBox>
            <SoftBox>
              <SoftBox display="flex" alignItems="center" gap={1}>
                <FieldLabel>{s.autoDeleteLabel}</FieldLabel>
                {expiryPlanless ? <PlanLockBadge testId="gallery-settings-auto-delete-lock" /> : null}
              </SoftBox>
              <Select
                fullWidth
                input={<SoftInput />}
                value={form.autoDelete}
                disabled={!form.expiresDate}
                onChange={(event) =>
                  set('autoDelete', event.target.value as GallerySettingsForm['autoDelete'])
                }
                inputProps={{ 'aria-label': s.autoDeleteLabel }}
                data-testid="gallery-settings-auto-delete"
              >
                {GALLERY_AUTO_DELETE.map((value) => (
                  <MenuItem key={value} value={value} disabled={autoDeleteLocked(value)}>
                    {s.autoDelete[value]}
                  </MenuItem>
                ))}
              </Select>
              <SoftTypography variant="caption" color="text">
                {!form.expiresDate
                  ? s.autoDeleteNeedsExpiry
                  : form.autoDelete !== 'OFF'
                  ? s.autoDeleteHint
                  : ''}
              </SoftTypography>
            </SoftBox>
          </Group>
          <Group title={s.guests}>
            <SoftBox>
              <SoftBox display="flex" alignItems="center" gap={1}>
                <FieldLabel>{s.downloadsLabel}</FieldLabel>
                {downloadsPlanless ? <PlanLockBadge testId="gallery-settings-downloads-lock" /> : null}
              </SoftBox>
              <Select
                fullWidth
                input={<SoftInput />}
                value={form.downloads}
                onChange={(event) =>
                  set('downloads', event.target.value as GallerySettingsForm['downloads'])
                }
                inputProps={{ 'aria-label': s.downloadsLabel }}
                data-testid="gallery-settings-downloads"
              >
                {GALLERY_DOWNLOADS.map((value) => (
                  <MenuItem key={value} value={value} disabled={downloadsLocked(value)}>
                    {s.downloads[value]}
                  </MenuItem>
                ))}
              </Select>
            </SoftBox>
            <SoftBox>
              <FieldLabel>{s.languageLabel}</FieldLabel>
              <Select
                fullWidth
                displayEmpty
                input={<SoftInput />}
                value={form.language}
                onChange={(event) =>
                  set('language', event.target.value as GallerySettingsForm['language'])
                }
                inputProps={{ 'aria-label': s.languageLabel }}
                data-testid="gallery-settings-language"
              >
                <MenuItem value="">{s.languages.SITE}</MenuItem>
                {GALLERY_LANGUAGES.map((value) => (
                  <MenuItem key={value} value={value}>
                    {s.languages[value]}
                  </MenuItem>
                ))}
              </Select>
              <SoftTypography variant="caption" color="text">
                {s.languageHint}
              </SoftTypography>
            </SoftBox>
            <SoftBox>
              <SoftBox display="flex" alignItems="center" gap={1}>
                <FieldLabel htmlFor="gallery-settings-limit">{s.selectionLimitLabel}</FieldLabel>
                {limitPlanless ? <PlanLockBadge testId="gallery-settings-limit-lock" /> : null}
              </SoftBox>
              <SoftInput
                id="gallery-settings-limit"
                type="number"
                value={form.selectionLimit}
                disabled={limitLocked}
                error={limitInvalid}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  set('selectionLimit', event.target.value)
                }
                inputProps={{ min: 1, step: 1, disabled: limitLocked, 'data-testid': 'gallery-settings-limit' }}
              />
              {limitInvalid ? (
                <SoftTypography variant="caption" color="error">
                  {s.selectionLimitInvalid}
                </SoftTypography>
              ) : null}
            </SoftBox>
            <SoftBox>
              <FieldLabel>{s.guestHintsLabel}</FieldLabel>
              <SoftBox display="flex" alignItems="center" gap={1}>
                <Switch
                  checked={form.guestHints}
                  onChange={(event) => set('guestHints', event.target.checked)}
                  inputProps={{ 'aria-label': s.guestHintsSwitch }}
                  data-testid="gallery-settings-guest-hints-switch"
                />
                <SoftTypography variant="button" fontWeight="regular">
                  {s.guestHintsSwitch}
                </SoftTypography>
              </SoftBox>
              <SoftTypography variant="caption" color="text">
                {s.guestHintsHint}
              </SoftTypography>
            </SoftBox>
            <SoftBox>
              <FieldLabel>{s.allowHidingLabel}</FieldLabel>
              <SoftBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <Switch
                  checked={form.allowHiding}
                  disabled={hidingLocked}
                  onChange={(event) => set('allowHiding', event.target.checked)}
                  inputProps={{ 'aria-label': s.allowHidingSwitch }}
                  data-testid="gallery-settings-allow-hiding-switch"
                />
                <SoftTypography variant="button" fontWeight="regular">
                  {s.allowHidingSwitch}
                </SoftTypography>
                {hidingLocked ? <PlanLockBadge testId="gallery-settings-allow-hiding-lock" /> : null}
              </SoftBox>
              <SoftTypography variant="caption" color="text">
                {hidingLocked ? t.plan.lockedHint : s.allowHidingHint}
              </SoftTypography>
            </SoftBox>
          </Group>
        </SoftBox>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <SoftButton
          variant="outlined"
          color="error"
          onClick={() => setIsDeleteOpen(true)}
          disabled={isSaving || isDeleting}
          data-testid="gallery-delete"
        >
          {s.deleteGallery}
        </SoftButton>
        <SoftBox flex={1} />
        <SoftButton
          variant="gradient"
          color="info"
          onClick={save}
          disabled={isSaving || limitInvalid}
          data-testid="gallery-settings-done"
        >
          {isSaving ? s.saving : s.done}
        </SoftButton>
      </DialogActions>
      <ConfirmDialog
        open={isDeleteOpen}
        title={interpolateTemplate(s.deleteTitle, { title: gallery.title.trim() || t.untitled })}
        body={plural(s.deleteBody, gallery.itemCount)}
        confirmLabel={s.deleteConfirm}
        cancelLabel={t.cancel}
        busy={isDeleting}
        testId="gallery-delete-dialog"
        onCancel={() => setIsDeleteOpen(false)}
        onConfirm={remove}
      />
    </Dialog>
  );
}
